import {
  IncidentNotificationState,
  IncidentResponderAcknowledgementState,
  IncidentResponderKind,
  IncidentResponderRole,
  IncidentStatus,
  IncidentTaskEscalationKind,
  IncidentTaskStatus,
  PolicyClass
} from "@no-busy-work/ops-bridge-protocol";
import type { IncidentDetail, IncidentResponderAssignment, IncidentTask } from "@no-busy-work/ops-bridge-protocol";
import {
  CLOSEOUT_GATE,
  CLOSEOUT_GATE_ORDER,
  OUTAGE_NOTICE_STATE,
  UI_TONE,
  WORKFLOW_CLEAR_STATES,
  WORKFLOW_STATUS
} from "./OpsCenter.Config";
import { actorRefLabel } from "./OpsCenter.ActorRefs";
import { formatCopy } from "./OpsCenter.Format";
import { TAB_ID } from "./OpsCenter.Types";
import type { ActiveTaskProjection, OpsCenterCopy, ResponderRow, TabId, UiTone } from "./OpsCenter.Types";
import { statusLabel } from "./i18n/OpsCenter.I18n";

const WORKER_REMEDIATION_ACTION_KIND = "worker_remediation_session";

const INCIDENT_NOTIFICATION_READY_STATES = new Set<string>([
  IncidentNotificationState.Sent,
  IncidentNotificationState.NotRequired
]);

const OUTAGE_NOTICE_READY_STATES = new Set<IncidentDetail["communicationSummary"]["outageNoticeState"]>([
  OUTAGE_NOTICE_STATE.Posted,
  OUTAGE_NOTICE_STATE.NotRequired
]);

export interface ResolutionReadinessRow {
  readonly id: string;
  readonly label: string;
  readonly summary: string;
  readonly ready: boolean;
  readonly tone: UiTone;
  readonly actionLabel?: string;
  readonly actionTab?: TabId;
}

export interface ResolutionReadinessProjection {
  readonly ready: boolean;
  readonly blockers: readonly string[];
  readonly rows: readonly ResolutionReadinessRow[];
}

export function resolutionReadiness(detail: IncidentDetail, rootCause: string, copy: OpsCenterCopy): ResolutionReadinessProjection {
  const summary = detail.communicationSummary;
  const actionItems = detail.recoveryProposals.flatMap((proposal) => proposal.actions);
  const approvalRows = actionItems.filter((row) => row.requiresApproval && !workflowActionCleared(row.status, row.approvalState));
  const openDecisionRows = detail.decisions.filter((row) => !workflowStateCleared(row.state) && !workflowStateCleared(row.approvalState));
  const codeBridgeRows = actionItems.filter((row) => row.kind === WORKER_REMEDIATION_ACTION_KIND && row.status !== WORKFLOW_STATUS.Blocked && !workflowActionCleared(row.status, row.approvalState));
  const healthIsVerified = healthVerified(detail);
  const rootCauseRecorded = Boolean(rootCause.trim()) || evidenceOrTimelineIncludes(detail, ["root cause", "rootcause", "root_cause"]);
  const remediationProofIsRecorded = remediationProofRecorded(detail);
  const verificationEvidenceIsRecorded = verificationEvidenceRecorded(detail);
  const outageNoticeIsSatisfied = outageNoticeSatisfied(summary.outageNoticeState);
  const customerNotificationsSatisfied = communicationStateSatisfied(summary.customerNotificationState);
  const resolutionNotificationsSatisfied = communicationStateSatisfied(summary.resolutionNotificationState) && summary.deliveryRiskCount === 0;
  const approvalQueueClear = approvalRows.length === 0 && openDecisionRows.length === 0;
  const codeBridgeSatisfied = codeBridgeRows.length === 0 || remediationProofIsRecorded;
  const rows: ResolutionReadinessRow[] = [
    readinessRow(
      CLOSEOUT_GATE.HealthVerification,
      copy.labels.healthVerified,
      healthIsVerified ? copy.labels.healthVerificationReady : copy.labels.healthVerificationPending,
      healthIsVerified,
      copy.labels.openEvidence,
      TAB_ID.Evidence
    ),
    readinessRow(
      CLOSEOUT_GATE.RootCause,
      copy.labels.rootCause,
      rootCauseRecorded ? copy.labels.rootCauseReady : copy.labels.rootCausePending,
      rootCauseRecorded,
      copy.labels.editRootCause,
      TAB_ID.Resolution
    ),
    readinessRow(
      CLOSEOUT_GATE.RemediationProof,
      copy.labels.remediationProof,
      remediationProofIsRecorded ? copy.labels.remediationProofReady : copy.labels.remediationProofPending,
      remediationProofIsRecorded,
      copy.labels.openEvidence,
      TAB_ID.Evidence
    ),
    readinessRow(
      CLOSEOUT_GATE.VerificationEvidence,
      copy.labels.verificationEvidence,
      verificationEvidenceIsRecorded ? copy.labels.verificationEvidenceReady : copy.labels.verificationEvidencePending,
      verificationEvidenceIsRecorded,
      copy.labels.openEvidence,
      TAB_ID.Evidence
    ),
    readinessRow(
      CLOSEOUT_GATE.OutageNotice,
      copy.labels.outageNotice,
      outageNoticeIsSatisfied ? copy.labels.outageNoticeSatisfied : copy.labels.outageNoticePending,
      outageNoticeIsSatisfied,
      copy.labels.openComms,
      TAB_ID.Comms
    ),
    readinessRow(
      CLOSEOUT_GATE.CustomerNotifications,
      copy.labels.customerNotifications,
      customerNotificationsSatisfied ? copy.labels.customerNotificationsSatisfied : copy.labels.customerNotificationsPending,
      customerNotificationsSatisfied,
      copy.labels.openComms,
      TAB_ID.Comms
    ),
    readinessRow(
      CLOSEOUT_GATE.ResolutionNotifications,
      copy.labels.resolutionNotifications,
      resolutionNotificationsSatisfied ? copy.labels.resolutionNotificationsSatisfied : copy.labels.resolutionNotificationsPending,
      resolutionNotificationsSatisfied,
      copy.labels.openComms,
      TAB_ID.Comms
    ),
    readinessRow(
      CLOSEOUT_GATE.OwnerAcknowledgement,
      copy.labels.ownerAcknowledgement,
      summary.ownerAcknowledged ? copy.labels.ownerAckSatisfied : copy.labels.ownerAckPending,
      summary.ownerAcknowledged,
      copy.labels.openActions,
      TAB_ID.Actions
    ),
    readinessRow(
      CLOSEOUT_GATE.ApprovalQueue,
      copy.sections.approvalQueue,
      approvalQueueClear ? copy.labels.approvalQueueClear : copy.labels.approvalQueuePending,
      approvalQueueClear,
      copy.labels.openActions,
      TAB_ID.Actions
    ),
    readinessRow(
      CLOSEOUT_GATE.CodeBridge,
      copy.sections.codeBridge,
      codeBridgeSatisfied ? copy.labels.codeBridgeSatisfied : copy.labels.codeBridgePending,
      codeBridgeSatisfied,
      copy.labels.openActions,
      TAB_ID.Actions
    )
  ];
  const blockers = rows.filter((row) => !row.ready).map((row) => row.label);
  return { ready: blockers.length === 0, blockers, rows };
}

export function activeTaskProjection(detail: IncidentDetail, rootCause: string, copy: OpsCenterCopy): ActiveTaskProjection | null {
  const readiness = resolutionReadiness(detail, rootCause, copy);
  const providedTask = detail.activeTask && !incidentTaskCleared(detail.activeTask) ? detail.activeTask : null;
  const gate = providedTask ? readiness.rows.find((row) => row.id === providedTask.gateId) ?? firstActiveGate(readiness) : firstActiveGate(readiness);
  if (!gate) {
    return null;
  }
  const rawTask = providedTask ?? protocolActiveTask(detail, gate) ?? fallbackIncidentTask(detail, gate, copy);
  const task = localizeIncidentTask(rawTask, detail, copy);
  const owner =
    responderRows(detail, rootCause, copy).find((row) => row.responderRef === task.ownerResponderRef || row.role === task.responderRole) ?? null;
  return {
    task,
    owner,
    gateReady: gate.ready,
    gateTone: gate.tone
  };
}

export function responderRows(detail: IncidentDetail, rootCause: string, copy: OpsCenterCopy): readonly ResponderRow[] {
  const readiness = resolutionReadiness(detail, rootCause, copy);
  const providedTask = detail.activeTask && !incidentTaskCleared(detail.activeTask) ? detail.activeTask : null;
  const gate = providedTask ? readiness.rows.find((row) => row.id === providedTask.gateId) ?? firstActiveGate(readiness) : firstActiveGate(readiness);
  const task = providedTask ?? (gate ? protocolActiveTask(detail, gate) ?? fallbackIncidentTask(detail, gate, copy) : null);
  const assignments = detail.responders?.length ? detail.responders : fallbackResponderAssignments(detail, gate, task, copy);
  return assignments.map((assignment) => responderRow(assignment, copy, detail));
}

function firstActiveGate(readiness: ResolutionReadinessProjection): ResolutionReadinessRow | null {
  const orderedRows = closeoutWorkflowRows(readiness);
  return orderedRows.find((row) => !row.ready) ?? orderedRows[0] ?? null;
}

function protocolActiveTask(detail: IncidentDetail, gate: ResolutionReadinessRow): IncidentTask | null {
  if (detail.activeTask && detail.activeTask.gateId === gate.id && !incidentTaskCleared(detail.activeTask)) {
    return detail.activeTask;
  }
  return detail.tasks?.find((task) => task.gateId === gate.id && !incidentTaskCleared(task)) ?? null;
}

function incidentTaskCleared(task: IncidentTask): boolean {
  return task.status === IncidentTaskStatus.Completed || task.status === IncidentTaskStatus.Cancelled;
}

function fallbackIncidentTask(detail: IncidentDetail, gate: ResolutionReadinessRow, copy: OpsCenterCopy): IncidentTask {
  const authorityClass = authorityClassForGate(gate.id);
  const primaryCtaKind = escalationKindForGate(gate.id);
  return {
    id: `${detail.incident.id}:task:${gate.id}`,
    incidentId: detail.incident.id,
    gateId: gate.id,
    title: gate.label,
    summary: gate.summary,
    status: gate.ready ? IncidentTaskStatus.Completed : primaryCtaKind === IncidentTaskEscalationKind.Approval ? IncidentTaskStatus.WaitingApproval : IncidentTaskStatus.Blocked,
    authorityClass,
    responderRole: responderRoleForGate(gate.id),
    ownerResponderRef: ownerResponderRefForGate(detail, gate.id),
    primaryCta: primaryCtaForGate(gate, copy),
    primaryCtaKind,
    requiresApproval: authorityClass === PolicyClass.ExternalWrite || authorityClass === PolicyClass.CredentialAccess || authorityClass === PolicyClass.Destructive || authorityClass === PolicyClass.Deploy || authorityClass === PolicyClass.GitPublication,
    evidencePrompt: `${copy.labels.evidence}: ${gate.summary}`,
    evidenceRefs: [],
    createdAt: detail.incident.createdAt,
    updatedAt: detail.incident.updatedAt
  };
}

function fallbackResponderAssignments(detail: IncidentDetail, gate: ResolutionReadinessRow | null, task: IncidentTask | null, copy: OpsCenterCopy): readonly IncidentResponderAssignment[] {
  const incident = detail.incident;
  const accountability = incident.accountability;
  const activeRole = task?.responderRole ?? null;
  const rows: IncidentResponderAssignment[] = [
    responderAssignment(detail, "commander", accountability.incidentCommanderRef, IncidentResponderKind.Human, IncidentResponderRole.IncidentCommander, PolicyClass.SafeRead, gate, task, copy.status.acknowledged),
    responderAssignment(detail, "diagnostic", "ai:diagnostic", IncidentResponderKind.Ai, IncidentResponderRole.DiagnosticResponder, PolicyClass.SafeRead, activeRole === IncidentResponderRole.DiagnosticResponder ? gate : null, activeRole === IncidentResponderRole.DiagnosticResponder ? task : null, latestIncidentSignal(detail) ?? copy.actions.recordHealthCheck),
    responderAssignment(detail, "recovery", accountability.serviceOwnerRef, IncidentResponderKind.Human, IncidentResponderRole.RecoveryResponder, PolicyClass.BoundedWrite, activeRole === IncidentResponderRole.RecoveryResponder ? gate : null, activeRole === IncidentResponderRole.RecoveryResponder ? task : null, task?.summary ?? copy.status.pending),
    responderAssignment(detail, "communications", accountability.communicationsOwnerRef, IncidentResponderKind.Human, IncidentResponderRole.CommunicationsResponder, PolicyClass.ExternalWrite, activeRole === IncidentResponderRole.CommunicationsResponder ? gate : null, activeRole === IncidentResponderRole.CommunicationsResponder ? task : null, communicationResponderAction(detail, copy)),
    responderAssignment(detail, "workflow", "system:closeout-workflow", IncidentResponderKind.System, IncidentResponderRole.CustomerImpactResponder, PolicyClass.SafeRead, null, null, copy.sections.resolutionReadiness)
  ];
  if (accountability.handoffTargetRef || incident.runbook?.toolIds.includes("tool_inventory_code_bridge")) {
    rows.push(
      responderAssignment(detail, "code-bridge", accountability.handoffTargetRef ?? "code-bridge:ops", IncidentResponderKind.System, IncidentResponderRole.CodeBridgeResponder, PolicyClass.GitPublication, activeRole === IncidentResponderRole.CodeBridgeResponder ? gate : null, activeRole === IncidentResponderRole.CodeBridgeResponder ? task : null, copy.sections.codeBridge)
    );
  }
  if (incident.runbook?.toolIds.includes("tool_inventory_telnyx_freeswitch")) {
    rows.push(
      responderAssignment(detail, "vendor", "vendor:telnyx", IncidentResponderKind.Vendor, IncidentResponderRole.ExternalEscalationResponder, PolicyClass.ExternalWrite, activeRole === IncidentResponderRole.ExternalEscalationResponder ? gate : null, activeRole === IncidentResponderRole.ExternalEscalationResponder ? task : null, copy.toolProviders.telnyx_freeswitch ?? "Telnyx")
    );
  }
  return rows;
}

function responderAssignment(
  detail: IncidentDetail,
  suffix: string,
  responderRef: string,
  kind: IncidentResponderKind,
  role: IncidentResponderRole,
  authorityClass: PolicyClass,
  gate: ResolutionReadinessRow | null,
  task: IncidentTask | null,
  currentAction: string
): IncidentResponderAssignment {
  const assigned = Boolean(gate && task);
  return {
    id: `${detail.incident.id}:responder:${suffix}`,
    incidentId: detail.incident.id,
    responderRef,
    displayName: "",
    kind,
    role,
    authorityClass,
    assignedTaskId: assigned ? task?.id ?? null : null,
    assignedGateId: assigned ? gate?.id ?? null : null,
    acknowledgementState: assigned && !gate?.ready ? IncidentResponderAcknowledgementState.Pending : IncidentResponderAcknowledgementState.Acknowledged,
    blocked: assigned && gate ? !gate.ready : false,
    currentAction,
    escalationPolicy: task?.primaryCtaKind ?? IncidentTaskEscalationKind.None,
    lastActivityAt: detail.incident.updatedAt,
    auditRefs: [],
    evidenceRefs: []
  };
}

function responderRow(assignment: IncidentResponderAssignment, copy: OpsCenterCopy, detail: IncidentDetail): ResponderRow {
  return {
    id: assignment.id,
    responderRef: assignment.responderRef,
    label: assignment.displayName || actorRefLabel(assignment.responderRef, copy),
    kind: assignment.kind,
    role: assignment.role,
    roleLabel: statusLabel(copy, assignment.role),
    authorityClass: assignment.authorityClass,
    assignedTaskId: assignment.assignedTaskId,
    assignedGateId: assignment.assignedGateId,
    acknowledgementState: assignment.acknowledgementState,
    acknowledgementLabel: statusLabel(copy, assignment.acknowledgementState),
    blocked: assignment.blocked,
    currentAction: localizedTaskText(assignment.currentAction, detail, copy),
    escalationPolicy: assignment.escalationPolicy,
    lastActivityLabel: formatTime(assignment.lastActivityAt),
    tone: assignment.blocked ? UI_TONE.Warn : assignment.acknowledgementState === IncidentResponderAcknowledgementState.Pending ? UI_TONE.Neutral : UI_TONE.Ok
  };
}

function localizeIncidentTask(task: IncidentTask, detail: IncidentDetail, copy: OpsCenterCopy): IncidentTask {
  return {
    ...task,
    title: localizedTaskText(task.title, detail, copy),
    summary: localizedTaskText(task.summary, detail, copy),
    primaryCta: localizedTaskText(task.primaryCta, detail, copy),
    evidencePrompt: localizedTaskText(task.evidencePrompt, detail, copy)
  };
}

function localizedTaskText(value: string, detail: IncidentDetail, copy: OpsCenterCopy): string {
  const template = copy.tasks?.[value];
  if (!template) {
    return value;
  }
  const summary = detail.communicationSummary;
  return formatCopy(template, {
    approvals: String((detail.decisions?.length ?? 0) + (detail.ownerEscalations?.length ?? 0)),
    complaints: String(summary.complaintCount),
    deliveryRisks: String(summary.deliveryRiskCount),
    impacted: String(summary.impactedCustomerCount)
  });
}

function authorityClassForGate(gateId: string): PolicyClass {
  if (gateId === CLOSEOUT_GATE.ApprovalQueue || gateId === CLOSEOUT_GATE.OutageNotice || gateId === CLOSEOUT_GATE.CustomerNotifications || gateId === CLOSEOUT_GATE.ResolutionNotifications) {
    return PolicyClass.ExternalWrite;
  }
  if (gateId === CLOSEOUT_GATE.CodeBridge) {
    return PolicyClass.GitPublication;
  }
  if (gateId === CLOSEOUT_GATE.RemediationProof) {
    return PolicyClass.BoundedWrite;
  }
  return PolicyClass.SafeRead;
}

function escalationKindForGate(gateId: string): IncidentTaskEscalationKind {
  if (gateId === CLOSEOUT_GATE.ApprovalQueue || gateId === CLOSEOUT_GATE.OutageNotice) {
    return IncidentTaskEscalationKind.Approval;
  }
  if (gateId === CLOSEOUT_GATE.CustomerNotifications || gateId === CLOSEOUT_GATE.ResolutionNotifications || gateId === CLOSEOUT_GATE.OwnerAcknowledgement) {
    return IncidentTaskEscalationKind.HumanResponse;
  }
  if (gateId === CLOSEOUT_GATE.CodeBridge) {
    return IncidentTaskEscalationKind.CodeBridge;
  }
  return IncidentTaskEscalationKind.None;
}

function responderRoleForGate(gateId: string): IncidentResponderRole {
  if (gateId === CLOSEOUT_GATE.HealthVerification || gateId === CLOSEOUT_GATE.VerificationEvidence) {
    return IncidentResponderRole.DiagnosticResponder;
  }
  if (gateId === CLOSEOUT_GATE.RemediationProof) {
    return IncidentResponderRole.RecoveryResponder;
  }
  if (gateId === CLOSEOUT_GATE.ApprovalQueue) {
    return IncidentResponderRole.Approver;
  }
  if (gateId === CLOSEOUT_GATE.OutageNotice || gateId === CLOSEOUT_GATE.CustomerNotifications || gateId === CLOSEOUT_GATE.ResolutionNotifications) {
    return IncidentResponderRole.CommunicationsResponder;
  }
  if (gateId === CLOSEOUT_GATE.OwnerAcknowledgement) {
    return IncidentResponderRole.ServiceOwner;
  }
  if (gateId === CLOSEOUT_GATE.CodeBridge) {
    return IncidentResponderRole.CodeBridgeResponder;
  }
  return IncidentResponderRole.PostIncidentLearningResponder;
}

function ownerResponderRefForGate(detail: IncidentDetail, gateId: string): string {
  const accountability = detail.incident.accountability;
  if (gateId === CLOSEOUT_GATE.HealthVerification || gateId === CLOSEOUT_GATE.VerificationEvidence) {
    return "ai:diagnostic";
  }
  if (gateId === CLOSEOUT_GATE.CodeBridge) {
    return accountability.handoffTargetRef ?? "code-bridge:ops";
  }
  if (gateId === CLOSEOUT_GATE.OutageNotice || gateId === CLOSEOUT_GATE.CustomerNotifications || gateId === CLOSEOUT_GATE.ResolutionNotifications) {
    return accountability.communicationsOwnerRef;
  }
  if (gateId === CLOSEOUT_GATE.ApprovalQueue) {
    return approvalOwnerRef(detail);
  }
  if (gateId === CLOSEOUT_GATE.OwnerAcknowledgement || gateId === CLOSEOUT_GATE.RemediationProof) {
    return accountability.serviceOwnerRef;
  }
  return accountability.currentAssigneeRef || accountability.serviceOwnerRef;
}

function approvalOwnerRef(detail: IncidentDetail): string {
  const decision = detail.decisions.find((item) => !workflowStateCleared(item.state) && !workflowStateCleared(item.approvalState));
  if (!decision) {
    return detail.incident.accountability.communicationsOwnerRef;
  }
  if (decision.assignedRole === "admin") {
    return "operator:admin-approver";
  }
  if (decision.assignedRole === "owner") {
    return detail.incident.accountability.serviceOwnerRef;
  }
  return detail.incident.accountability.communicationsOwnerRef;
}

function primaryCtaForGate(gate: ResolutionReadinessRow, copy: OpsCenterCopy): string {
  const labels: Readonly<Record<string, string>> = {
    [CLOSEOUT_GATE.HealthVerification]: copy.actions.recordHealthCheck,
    [CLOSEOUT_GATE.RemediationProof]: copy.actions.recordRemediationProof,
    [CLOSEOUT_GATE.VerificationEvidence]: copy.actions.recordVerification,
    [CLOSEOUT_GATE.ApprovalQueue]: copy.actions.clearApprovals,
    [CLOSEOUT_GATE.OutageNotice]: copy.actions.postOutageNotice,
    [CLOSEOUT_GATE.CustomerNotifications]: copy.actions.sendCustomerNotice,
    [CLOSEOUT_GATE.OwnerAcknowledgement]: copy.actions.acknowledgeOwner,
    [CLOSEOUT_GATE.RootCause]: copy.forms.rootCause,
    [CLOSEOUT_GATE.ResolutionNotifications]: copy.actions.sendResolutionNotice,
    [CLOSEOUT_GATE.CodeBridge]: copy.actions.recordCodeBridgeProof
  };
  return labels[gate.id] ?? gate.actionLabel ?? copy.actions.recordGate;
}

function communicationResponderAction(detail: IncidentDetail, copy: OpsCenterCopy): string {
  const summary = detail.communicationSummary;
  return `${copy.labels.notice}: ${statusLabel(copy, summary.outageNoticeState)} / ${copy.labels.resolution}: ${statusLabel(copy, summary.resolutionNotificationState)}`;
}

function workflowActionCleared(status: string, approvalState: string | null | undefined): boolean {
  return workflowStateCleared(status) || workflowStateCleared(approvalState);
}

function workflowStateCleared(value: string | null | undefined): boolean {
  return typeof value === "string" && WORKFLOW_CLEAR_STATES.has(value);
}

export function closeoutGateIsInteractive(row: ResolutionReadinessRow): boolean {
  return !row.ready;
}

export function closeoutWorkflowRows(readiness: ResolutionReadinessProjection): readonly ResolutionReadinessRow[] {
  return [...readiness.rows].sort((left, right) => closeoutGatePriority(left.id) - closeoutGatePriority(right.id));
}

function closeoutGatePriority(gateId: string): number {
  const priority = CLOSEOUT_GATE_ORDER.findIndex((item) => item === gateId);
  return priority < 0 ? CLOSEOUT_GATE_ORDER.length : priority;
}

function readinessRow(id: string, label: string, summary: string, ready: boolean, actionLabel?: string, actionTab?: TabId): ResolutionReadinessRow {
  return {
    id,
    label,
    summary,
    ready,
    tone: ready ? UI_TONE.Ok : UI_TONE.Warn,
    actionLabel,
    actionTab
  };
}

function healthVerified(detail: IncidentDetail): boolean {
  return detail.incident.status === IncidentStatus.Monitoring || detail.incident.status === IncidentStatus.Resolved || detail.incident.status === IncidentStatus.Closed || verificationEvidenceRecorded(detail);
}

function remediationProofRecorded(detail: IncidentDetail): boolean {
  return evidenceOrTimelineIncludes(detail, ["remediation", "mitigation", "recovery", "failover", "code bridge", "fix"]);
}

function verificationEvidenceRecorded(detail: IncidentDetail): boolean {
  return evidenceOrTimelineIncludes(detail, ["verification", "verified", "probe", "health", "passed"]);
}

function evidenceOrTimelineIncludes(detail: IncidentDetail, tokens: readonly string[]): boolean {
  return [
    ...detail.evidence.map((item) => `${item.kind} ${item.label} ${item.redactedBody}`),
    ...detail.timeline.map((item) => `${item.kind} ${item.redactedBody}`)
  ].some((value) => tokens.some((token) => value.toLowerCase().includes(token)));
}

function outageNoticeSatisfied(state: string): boolean {
  return OUTAGE_NOTICE_READY_STATES.has(state as IncidentDetail["communicationSummary"]["outageNoticeState"]);
}

function communicationStateSatisfied(state: string): boolean {
  return INCIDENT_NOTIFICATION_READY_STATES.has(state);
}

function latestIncidentSignal(detail: IncidentDetail): string | null {
  return detail.signals[detail.signals.length - 1]?.redactedSummary ?? null;
}

function formatTime(iso: string): string {
  return new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
}
